import React from "react";
import clsx from "clsx";
import { ConsentBannerScreenPosition } from "../../../../common/service/server/dto/CMPSettingsDto";

const BANNER_POSITION_CLASSES: Record<ConsentBannerScreenPosition, string> = {
  [ConsentBannerScreenPosition.BOTTOM_LEFT]: "bottom-[7px] left-[7px]",
  [ConsentBannerScreenPosition.BOTTOM_CENTER]: "bottom-[7px] left-[28%]",
  [ConsentBannerScreenPosition.BOTTOM_RIGHT]: "bottom-[7px] right-[7px]",
  [ConsentBannerScreenPosition.MIDDLE_CENTER]: "left-[28%] top-[22px]",
};

/**
 * A tiny web page with the banner drawn on it, for the visual option cards.
 * @param bannerColor The banner's own translucent background (the CMP's #eeeeee or #222222, at 60% opacity), drawn over
 * page content so the translucency shows. White when unset.
 */
export const BannerThumbnail = ({
  position = ConsentBannerScreenPosition.BOTTOM_LEFT,
  bannerColor,
}: {
  position?: ConsentBannerScreenPosition;
  bannerColor?: "light" | "dark";
}) => (
  <span
    aria-hidden="true"
    className="relative block h-[64px] overflow-hidden rounded-[6px] border border-solid border-neutral-200 bg-neutral-100"
  >
    <span className="absolute left-[9px] top-[9px] h-[5px] w-[52%] rounded-full bg-neutral-300" />
    <span className="absolute left-[9px] top-[19px] h-[5px] w-[40%] rounded-full bg-neutral-200" />
    <span className="absolute left-[9px] top-[29px] h-[5px] w-[46%] rounded-full bg-neutral-200" />
    <span className="absolute left-[9px] top-[39px] h-[5px] w-[72%] rounded-full bg-neutral-300" />
    <span className="absolute left-[9px] top-[49px] h-[5px] w-[60%] rounded-full bg-neutral-300" />
    <span
      className={clsx(
        "absolute h-[20px] w-[44%] rounded-[4px] shadow-[0_1px_3px_rgba(26,23,38,0.28)]",
        bannerColor === "light" && "bg-[#eeeeee]/60",
        bannerColor === "dark" && "bg-[#222222]/60",
        !bannerColor && "bg-white",
        BANNER_POSITION_CLASSES[position],
      )}
    >
      {bannerColor && (
        <span
          className={clsx(
            "absolute left-[5px] top-[5px] h-[3px] w-[48%] rounded-full",
            bannerColor === "dark" ? "bg-white/60" : "bg-neutral-400",
          )}
        />
      )}
      <span className="absolute bottom-[5px] right-[5px] h-[6px] w-[14px] rounded-full bg-primary-600" />
    </span>
  </span>
);

/** A button as it looks on the live banner (these colors belong to the banner, not the app). */
export const BannerButtonPreview = ({
  children,
  variant = "button",
}: {
  children: React.ReactNode;
  variant?: "button" | "link";
}) =>
  variant === "link" ? (
    <span className="whitespace-nowrap px-[4px] text-[13px] leading-[20px] text-[#0a66c2]">
      {children}
    </span>
  ) : (
    <span className="inline-flex h-[28px] items-center whitespace-nowrap rounded-full border border-solid border-[#e4deef] bg-white px-[14px] text-[13px] font-normal text-[#3f3854] shadow-[0_4px_10px_rgba(0,0,0,0.10)]">
      {children}
    </span>
  );

/** A row of banner buttons on a white pill, like the bottom of the live banner. */
export const BannerButtonRow = ({ children }: { children: React.ReactNode }) => (
  <span className="flex min-w-0 flex-wrap items-center gap-[8px] rounded-full bg-white px-[12px] py-[8px] shadow-[0_2px_10px_rgba(26,23,38,0.10)]">
    {children}
  </span>
);
